import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  ChevronDown,
  ExternalLink,
  Linkedin,
  Mail,
  MapPin,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { toStatus, type ApiItem } from "../utils/applicationCards";
import {
  DETECT_STALLED_AFTER_MS,
  appliedRoleIds,
  detectionWait,
  daysUntil,
  openRoles,
  roleLevelText,
  shortTimeAgo,
  soonestDeadline,
  stagesReached,
  stepsOf,
  toMillis,
  trackingState,
  openRolesNewFirst,
  unseenNewRoles,
  type DreamBoard,
  type DreamCompany,
  type OpenRole,
  type Step,
} from "../utils/dreamCompanies";
import type { TargetRole } from "./addTargetRole";
import type { ContactPerson, ContactStatus } from "./addContactPerson";
import { MENU_ITEM, OUTLINE_TAG, STATUS_TAG, TAG, formatAppDate } from "./applicationShared";
import { EmptyColumn } from "./TrackerBoard";
import { CompanyLogo, DANGER_ITEM, DeadlineChip, NextSteps, RowMenu, SectionLabel, StageBar } from "./dreamShared";
import { DropdownMenuItem, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { cn } from "../lib/utils";
import type { RoleAction } from "../utils/newRoles";

// Side panel for one dream company: next step, progress, its tracker
// applications, the roles you're watching and the people you know there.

/** Open roles shown before "View all". */
const ROLES_SHOWN = 4;

export const CONTACT_STATUS_ORDER: ContactStatus[] = ["to-reach-out", "reached-out", "talked"];

const CONTACT_STATUS: Record<ContactStatus, { label: string; className: string }> = {
  "to-reach-out": { label: "To reach out", className: "bg-[#F2EDE4] text-melt-text1" },
  "reached-out": { label: "Reached out", className: "bg-melt-peach text-[#8A5210]" },
  talked: { label: "Talked", className: "bg-[#EAF4EF] text-[#2F6B54]" },
};

const LINK_CHIP =
  "inline-flex items-center gap-1 rounded-full border border-melt-hair bg-white px-2.5 py-1 text-[12px] font-medium text-melt-text2 transition-colors hover:border-melt-caramel hover:text-melt-text1";

/** Small + like the tracker's column headers. */
const ADD_BUTTON =
  "grid h-6 w-6 place-items-center rounded-[7px] text-melt-text3 transition-colors hover:bg-melt-text1/[.07] hover:text-melt-text1";

const ROW = "flex items-start gap-3 rounded-[12px] border border-melt-text1/[.1] bg-melt-paper p-3";

function findRoleLinks(c: DreamCompany) {
  return [{ label: "Google", href: `https://www.google.com/search?q=${encodeURIComponent(c.name + " careers")}` }];
}

function findPeopleLinks(c: DreamCompany, school: string) {
  const search = (s: string) => `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(s)}`;
  const firstRole = openRoles(c)[0]?.title || (c.roles || [])[0]?.title;
  const links = [
    { label: "Alumni", href: search([school.trim(), c.name].filter(Boolean).join(" ")) },
    { label: "Recruiters", href: search(`${c.name} recruiter`) },
  ];
  if (firstRole) links.push({ label: "Team", href: search(`${c.name} ${firstRole}`) });
  return links;
}

const withProtocol = (url: string) => (url.startsWith("http") ? url : `https://${url}`);

/** A role saved by hand (companies we can't track automatically). */
function RoleRow({ role, onEdit, onDelete }: { role: TargetRole; onEdit: () => void; onDelete: () => void }) {
  const days = daysUntil(role.deadline);
  return (
    <div className={ROW}>
      <div className="min-w-0 flex-1">
        <span className="text-[13.5px] font-medium leading-snug">{role.title}</span>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-melt-text3">
          {role.location && (
            <span className="inline-flex items-center gap-1">
              <MapPin size={12} strokeWidth={1.75} />
              {role.location}
            </span>
          )}
          {days !== null && (days < 0 ? <span className={OUTLINE_TAG}>Closed</span> : <DeadlineChip days={days} />)}
          {role.url && (
            <a
              href={withProtocol(role.url)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 hover:text-melt-text1"
            >
              View posting
              <ExternalLink size={11} strokeWidth={1.75} />
            </a>
          )}
        </div>
        {role.notes && <p className="mt-1.5 whitespace-pre-wrap text-[12.5px] leading-relaxed text-melt-text2">{role.notes}</p>}
      </div>

      <RowMenu label={`Options for ${role.title}`}>
        <DropdownMenuItem onSelect={onEdit} className={MENU_ITEM}>
          <Pencil size={15} strokeWidth={1.75} />
          Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-melt-hair" />
        <DropdownMenuItem onSelect={onDelete} className={DANGER_ITEM}>
          <Trash2 size={15} strokeWidth={1.75} />
          Delete
        </DropdownMenuItem>
      </RowMenu>
    </div>
  );
}

/** A role saved from New roles on Home. Remove takes it off the saved list. */
function SavedFeedRoleRow({ role, onRemove }: { role: RoleAction; onRemove: () => void }) {
  return (
    <div className={ROW}>
      <div className="min-w-0 flex-1">
        <span className="text-[13.5px] font-medium leading-snug">{role.title}</span>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-melt-text3">
          {role.location && (
            <span className="inline-flex items-center gap-1">
              <MapPin size={12} strokeWidth={1.75} />
              {role.location}
            </span>
          )}
          {role.url && (
            <a
              href={role.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 hover:text-melt-text1"
            >
              View posting
              <ExternalLink size={11} strokeWidth={1.75} />
            </a>
          )}
          <span>Saved from New roles</span>
        </div>
      </div>

      <RowMenu label={`Options for ${role.title}`}>
        <DropdownMenuItem onSelect={onRemove} className={DANGER_ITEM}>
          <Trash2 size={15} strokeWidth={1.75} />
          Remove from saved
        </DropdownMenuItem>
      </RowMenu>
    </div>
  );
}

/** An open role from the company's job board that matches the user. Opens the posting to apply. */
function OpenRoleRow({ role, isNew, applied }: { role: OpenRole; isNew: boolean; applied: boolean }) {
  const posted = formatAppDate(role.firstPublished || role.firstSeenAt || "");
  const levelText = roleLevelText(role);
  return (
    <a
      href={role.url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(ROW, "items-center transition-colors hover:border-melt-text1/20", isNew && "border-melt-caramel/40")}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {isNew && !applied && <span className="h-[7px] w-[7px] flex-none rounded-full bg-melt-caramel" />}
          <span className="text-[13.5px] font-medium leading-snug">{role.title}</span>
          {applied && (
            <span className={cn(TAG, "flex-none", STATUS_TAG.submitted.className)} title="You applied (from your tracker)">
              Applied
            </span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-melt-text3">
          {role.locations.length > 0 && (
            <span className="inline-flex min-w-0 items-center gap-1">
              <MapPin size={12} strokeWidth={1.75} className="flex-none" />
              <span className="truncate">{role.locations.join(" · ")}</span>
            </span>
          )}
          {posted && <span className="font-plex text-[11px]">posted {posted}</span>}
          {levelText && <span>{levelText}</span>}
        </div>
      </div>
      <span
        className={cn(
          "inline-flex flex-none items-center gap-1 text-[12.5px] font-medium",
          applied ? "text-melt-text3" : "text-melt-caramel"
        )}
      >
        {applied ? "View" : "Apply"}
        <ExternalLink size={12} strokeWidth={1.75} />
      </span>
    </a>
  );
}

function PersonRow({
  person,
  onEdit,
  onDelete,
  onCycleStatus,
}: {
  person: ContactPerson;
  onEdit: () => void;
  onDelete: () => void;
  onCycleStatus: () => void;
}) {
  const status = CONTACT_STATUS[person.status] || CONTACT_STATUS["to-reach-out"];
  return (
    <div className={ROW}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-1.5">
          <span className="text-[13.5px] font-medium">{person.name}</span>
          {person.role && <span className="text-[12.5px] text-melt-text3">· {person.role}</span>}
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-melt-text3">
          <button
            type="button"
            onClick={onCycleStatus}
            title="Click to move to the next status"
            className={cn(TAG, status.className, "transition-opacity hover:opacity-80")}
          >
            {status.label}
          </button>
          {person.linkedin && (
            <a
              href={withProtocol(person.linkedin)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 hover:text-melt-text1"
            >
              <Linkedin size={12} strokeWidth={1.75} />
              LinkedIn
            </a>
          )}
          {person.email && (
            <a href={`mailto:${person.email}`} className="inline-flex items-center gap-1 hover:text-melt-text1">
              <Mail size={12} strokeWidth={1.75} />
              Email
            </a>
          )}
        </div>
        {person.notes && (
          <p className="mt-1.5 whitespace-pre-wrap text-[12.5px] leading-relaxed text-melt-text2">{person.notes}</p>
        )}
      </div>

      <RowMenu label={`Options for ${person.name}`}>
        <DropdownMenuItem onSelect={onEdit} className={MENU_ITEM}>
          <Pencil size={15} strokeWidth={1.75} />
          Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-melt-hair" />
        <DropdownMenuItem onSelect={onDelete} className={DANGER_ITEM}>
          <Trash2 size={15} strokeWidth={1.75} />
          Delete
        </DropdownMenuItem>
      </RowMenu>
    </div>
  );
}


export type DreamCompanySheetProps = {
  company: DreamCompany | null;
  applications: ApiItem[];
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onStepsChange: (steps: Step[]) => void;
  onAddRole: () => void;
  onEditRole: (role: TargetRole) => void;
  onDeleteRole: (role: TargetRole) => void;
  onOpenTracker: () => void;
  /** boards/{boardKey}, for "Checked 2h ago" */
  board?: DreamBoard;
  /** A job board check for this company is running right now */
  detecting: boolean;
  onRetryDetection: () => void;
  school: string;
  onSchoolChange: (school: string) => void;
  onAddPerson: () => void;
  onEditPerson: (person: ContactPerson) => void;
  onDeletePerson: (person: ContactPerson) => void;
  onCyclePersonStatus: (person: ContactPerson) => void;
  /** Roles at this company saved from New roles on Home */
  savedRoles?: RoleAction[];
  onUnsaveRole?: (role: RoleAction) => void;
};

export default function DreamCompanySheet(props: DreamCompanySheetProps) {
  const { company: openCompany, applications, onClose } = props;
  // Keep showing the last company while the panel slides out
  const [company, setCompany] = useState<DreamCompany | null>(openCompany);
  useEffect(() => {
    if (openCompany) setCompany(openCompany);
  }, [openCompany]);

  const roles = company?.roles || [];
  const savedRoles = props.savedRoles || [];
  const people = company?.people || [];
  const deadline = company ? soonestDeadline(company) : null;
  const state = company ? trackingState(company) : "pending";
  const stalled = company ? detectionWait(company) > DETECT_STALLED_AFTER_MS : false;
  // The "new" dots stay put while it's open: seenAt only changes on close
  const matching = company ? openRolesNewFirst(company) : [];
  const unseen = new Set(company ? unseenNewRoles(company).map((r) => r.id) : []);
  const applied = appliedRoleIds(matching, applications);
  // Long lists start collapsed to the first few (new roles sort first)
  const [showAllRoles, setShowAllRoles] = useState(false);
  useEffect(() => setShowAllRoles(false), [company?.id]);
  const shownRoles = showAllRoles ? matching : matching.slice(0, ROLES_SHOWN);
  const checkedAt = toMillis(props.board?.lastCheckedAt);

  // Soonest deadline first
  const sortedRoles = [...roles].sort((a, b) => {
    const da = daysUntil(a.deadline) ?? Infinity;
    const db = daysUntil(b.deadline) ?? Infinity;
    return da - db;
  });
  const sortedPeople = [...people].sort(
    (a, b) => CONTACT_STATUS_ORDER.indexOf(a.status) - CONTACT_STATUS_ORDER.indexOf(b.status)
  );

  return (
    <DialogPrimitive.Root open={Boolean(openCompany)} onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/10 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[500px] flex-col border-l border-melt-hair bg-[#FDFBF8] font-grotesk text-melt-text1 shadow-[-24px_0_48px_-24px_rgba(92,52,23,.35)] outline-none duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right">
          {company && (
            <>
              <header className="border-b border-melt-hair px-6 pb-5 pt-6">
                <div className="flex items-start gap-3">
                  <CompanyLogo name={company.name} domain={company.domain} size={44} />
                  <div className="min-w-0 flex-1">
                    <DialogPrimitive.Title className="truncate text-[22px] font-semibold leading-tight tracking-[-.02em]">
                      {company.name}
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description asChild>
                      {company.domain ? (
                        <a
                          href={withProtocol(company.domain)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-0.5 inline-flex items-center gap-1 text-[13px] text-melt-text2 hover:text-melt-text1"
                        >
                          {company.domain}
                          <ExternalLink size={12} strokeWidth={1.75} />
                        </a>
                      ) : (
                        <p className="mt-0.5 text-[13px] text-melt-text3">Dream company</p>
                      )}
                    </DialogPrimitive.Description>
                  </div>
                  <div className="-mr-2 flex items-center gap-0.5">
                    <RowMenu label={`Options for ${company.name}`}>
                      <DropdownMenuItem onSelect={props.onEdit} className={MENU_ITEM}>
                        <Pencil size={15} strokeWidth={1.75} />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-melt-hair" />
                      <DropdownMenuItem onSelect={props.onDelete} className={DANGER_ITEM}>
                        <Trash2 size={15} strokeWidth={1.75} />
                        Delete
                      </DropdownMenuItem>
                    </RowMenu>
                    <DialogPrimitive.Close
                      aria-label="Close"
                      className="grid h-8 w-8 flex-none place-items-center rounded-lg text-melt-text3 transition-colors hover:bg-melt-text1/[.06] hover:text-melt-text1"
                    >
                      <X size={18} strokeWidth={1.75} />
                    </DialogPrimitive.Close>
                  </div>
                </div>

                {(deadline !== null || state === "untracked") && (
                  <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    {deadline !== null && <DeadlineChip days={deadline} />}
                    {state === "untracked" && <span className={OUTLINE_TAG}>Can’t track automatically</span>}
                  </div>
                )}
                {state === "tracked" && (
                  <p className="mt-2.5 font-plex text-[11px] text-melt-text3">
                    {checkedAt ? `checked ${shortTimeAgo(new Date(checkedAt).toISOString())}` : "first check soon"}
                  </p>
                )}
                <div className="mt-4">
                  <NextSteps steps={stepsOf(company)} onChange={props.onStepsChange} />
                </div>
                <div className="mt-4">
                  <StageBar reached={stagesReached(company, applications)} />
                </div>
              </header>

              <div className="scrollbar-thin flex-1 space-y-7 overflow-y-auto px-6 py-5">
                {company.notes && (
                  <p className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-melt-text2">{company.notes}</p>
                )}

                {applications.length > 0 && (
                  <section>
                    <SectionLabel count={applications.length}>In your tracker</SectionLabel>
                    <div className="mt-3 space-y-2">
                      {applications.map((app) => {
                        const status = toStatus(app.predicted_label) ?? "submitted";
                        return (
                          <button
                            key={app.id}
                            type="button"
                            onClick={props.onOpenTracker}
                            className={cn(ROW, "w-full items-center text-left transition-colors hover:border-melt-text1/20")}
                          >
                            <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium">
                              {app.role || "Role not detected"}
                            </span>
                            <span className={cn(TAG, "flex-none", STATUS_TAG[status].className)}>
                              {STATUS_TAG[status].label}
                            </span>
                            {app.date && (
                              <span className="flex-none font-plex text-[11px] text-melt-text3">{formatAppDate(app.date)}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </section>
                )}

                {state === "tracked" && (
                  <section>
                    <SectionLabel count={matching.length}>Open roles</SectionLabel>
                    <div className="mt-3 space-y-2">
                      {matching.length === 0 ? (
                        <EmptyColumn
                          title="Nothing open right now"
                          text={`Nothing at ${company.name} matches what you’re looking for. Caramel checks for new roles every few hours.`}
                        />
                      ) : (
                        shownRoles.map((role) => (
                          <OpenRoleRow
                            key={role.id}
                            role={role}
                            isNew={unseen.has(role.id)}
                            applied={applied.has(role.id)}
                          />
                        ))
                      )}
                    </div>
                    {matching.length > ROLES_SHOWN && (
                      <button
                        type="button"
                        onClick={() => setShowAllRoles((all) => !all)}
                        aria-expanded={showAllRoles}
                        className="mt-2 inline-flex items-center gap-1 rounded-lg px-1 py-1 text-[13px] font-medium text-melt-caramel transition-colors hover:text-melt-press"
                      >
                        <ChevronDown
                          size={15}
                          strokeWidth={2}
                          className={cn("transition-transform duration-150", showAllRoles && "rotate-180")}
                        />
                        {showAllRoles ? "Show fewer" : `View all ${matching.length} roles`}
                      </button>
                    )}
                    <p className="mt-2.5 text-[12px] leading-relaxed text-melt-text3">
                      Matched to the roles, job types and locations in{" "}
                      <Link to="/settings/job-search" className="font-medium text-melt-text2 underline-offset-2 hover:underline">
                        your settings
                      </Link>
                      .
                    </p>
                  </section>
                )}

                {state === "pending" && (
                  <section>
                    <SectionLabel>Open roles</SectionLabel>
                    <div className="mt-3">
                      {stalled && !props.detecting ? (
                        <EmptyColumn
                          title="Couldn’t check their job board"
                          text={
                            <>
                              Caramel’s server didn’t answer.
                              <button
                                type="button"
                                onClick={props.onRetryDetection}
                                className="mt-3 block w-full font-medium text-melt-caramel transition-colors hover:text-melt-press"
                              >
                                Try again
                              </button>
                            </>
                          }
                        />
                      ) : (
                        <div className="flex items-start gap-3 px-1 py-2" role="status">
                          {/* Same spinner as onboarding's setup checklist */}
                          <span className="mt-px h-[18px] w-[18px] flex-none animate-[spin_.8s_linear_infinite] rounded-full border-[1.5px] border-melt-caramel border-t-transparent" />
                          <div>
                            <p className="text-[15px] font-semibold text-melt-text1">Looking for their job board</p>
                            <p className="mt-1 text-[13px] leading-[1.45] text-melt-text2">This only takes a few seconds.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {/* Saved by hand (companies we can't track, or roles saved before tracking existed) and from New roles */}
                {(state === "untracked" || roles.length > 0 || savedRoles.length > 0) && (
                  <section>
                    <div className="flex items-center justify-between">
                      <SectionLabel count={roles.length + savedRoles.length}>
                        {state === "untracked" ? "Roles" : "Saved roles"}
                      </SectionLabel>
                      {state === "untracked" && (
                        <button type="button" onClick={props.onAddRole} aria-label="Add role" className={ADD_BUTTON}>
                          <Plus size={16} strokeWidth={1.75} />
                        </button>
                      )}
                    </div>
                    {state === "untracked" && (
                      <>
                        <p className="mt-2 text-[12.5px] leading-relaxed text-melt-text2">
                          Caramel can’t read {company.name}’s job board, so it can’t spot new roles. Keep the ones you’re
                          watching here.
                        </p>
                        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                          {findRoleLinks(company).map((link) => (
                            <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer" className={LINK_CHIP}>
                              {link.label}
                              <ExternalLink size={11} strokeWidth={1.75} />
                            </a>
                          ))}
                        </div>
                      </>
                    )}
                    <div className="mt-3 space-y-2">
                      {savedRoles.map((role) => (
                        <SavedFeedRoleRow key={role.id} role={role} onRemove={() => props.onUnsaveRole?.(role)} />
                      ))}
                      {sortedRoles.length === 0 && savedRoles.length === 0 ? (
                        <EmptyColumn title="No roles yet" text="Add one you’re watching with +, or search with the link above." />
                      ) : (
                        sortedRoles.map((role) => (
                          <RoleRow
                            key={role.id}
                            role={role}
                            onEdit={() => props.onEditRole(role)}
                            onDelete={() => props.onDeleteRole(role)}
                          />
                        ))
                      )}
                    </div>
                  </section>
                )}

                <section>
                  <div className="flex items-center justify-between">
                    <SectionLabel count={people.length}>People</SectionLabel>
                    <button type="button" onClick={props.onAddPerson} aria-label="Add person" className={ADD_BUTTON}>
                      <Plus size={16} strokeWidth={1.75} />
                    </button>
                  </div>
                  <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                    <input
                      value={props.school}
                      onChange={(e) => props.onSchoolChange(e.target.value)}
                      placeholder="Your school, for alumni"
                      aria-label="Your school"
                      className="h-[27px] w-[170px] rounded-full border border-melt-hair bg-white px-2.5 text-[12px] text-melt-text1 outline-none placeholder:text-melt-text3 focus:border-melt-caramel"
                    />
                    {findPeopleLinks(company, props.school).map((link) => (
                      <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer" className={LINK_CHIP}>
                        {link.label}
                        <ExternalLink size={11} strokeWidth={1.75} />
                      </a>
                    ))}
                  </div>
                  <div className="mt-3 space-y-2">
                    {sortedPeople.length === 0 ? (
                      <EmptyColumn title="No one yet" text="Add a contact with +, or find alumni and recruiters with the links above." />
                    ) : (
                      sortedPeople.map((person) => (
                        <PersonRow
                          key={person.id}
                          person={person}
                          onEdit={() => props.onEditPerson(person)}
                          onDelete={() => props.onDeletePerson(person)}
                          onCycleStatus={() => props.onCyclePersonStatus(person)}
                        />
                      ))
                    )}
                  </div>
                </section>
              </div>
            </>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
